import React, { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LogIn, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import LegalFooter from "@/components/LegalFooter";

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  if (user) return <Navigate to={user.role === "UITVOERDER" ? "/vandaag" : "/planbord"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password, remember);
    if (res.ok) {
      toast.success("Welkom terug");
      nav("/planbord");
    } else {
      toast.error(res.error || "Inloggen mislukt");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      {/* Left brand panel */}
      <div className="hidden md:flex bg-slate-900 text-white p-10 flex-col justify-between">
        <div className="flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center hover:opacity-80 transition" data-testid="brand-home-link">
            <img src="/logo-ploegbord.webp?v=8" alt="Ploegbord" className="h-14 w-auto" />
          </Link>
          <Link to="/" className="flex items-center gap-1.5 text-xs font-black uppercase tracking-widest text-slate-300 hover:text-orange-500 transition" data-testid="btn-back-to-site">
            <ArrowLeft size={14}/> Terug naar website
          </Link>
        </div>
        <div>
          <h1 className="pb-heading text-5xl font-black leading-tight text-white">
            Van kantoor naar<br /><span className="text-orange-500">de bouwplaats</span>.<br />In één blik.
          </h1>
          <p className="text-slate-300 mt-6 text-lg font-medium max-w-md">
            Plan ploegen, projecten en bussen op één centraal bord. Elke monteur ziet &lsquo;s ochtends direct: waar, met wie, welke auto, wat.
          </p>
          <div className="grid grid-cols-3 gap-3 mt-10">
            {[
              { c: "#64748B", l: "GEPLAND" },
              { c: "#3B82F6", l: "BEVESTIGD" },
              { c: "#F97316", l: "BEZIG" },
              { c: "#16A34A", l: "AFGEROND" },
              { c: "#DC2626", l: "CONFLICT" },
            ].map((s) => (
              <div key={s.l} className="border-2 border-white/30 rounded px-3 py-2">
                <div className="h-2 rounded-sm" style={{ background: s.c }} />
                <div className="text-xs mt-1 font-bold uppercase tracking-widest text-white">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="text-slate-400 text-xs font-semibold uppercase tracking-widest">Multi-tenant SaaS voor bouwbedrijven</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 md:p-10 bg-slate-950">
        <div className="w-full max-w-md space-y-6 text-white">
          <div className="md:hidden flex items-center justify-between mb-2">
            <img src="/logo-ploegbord.webp?v=8" alt="Ploegbord" className="h-10 w-auto" />
            <Link to="/" className="flex items-center gap-1 text-xs font-black uppercase tracking-widest text-slate-300 hover:text-orange-500" data-testid="btn-back-to-site-mobile">
              <ArrowLeft size={12}/> Terug
            </Link>
          </div>

          {/* Custom login form */}
          <form onSubmit={submit} className="space-y-3" data-testid="login-form">
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-slate-300">E-mailadres</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pb-input mt-1 !bg-slate-900 !border-slate-700 !text-white"
                required
                data-testid="login-email"
                autoComplete="email"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label className="text-xs font-bold uppercase tracking-widest text-slate-300">Wachtwoord</label>
                <a href="/wachtwoord-vergeten" className="text-xs font-bold text-orange-500 hover:underline" data-testid="forgot-link">Vergeten?</a>
              </div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pb-input mt-1 !bg-slate-900 !border-slate-700 !text-white"
                required
                data-testid="login-password"
                autoComplete="current-password"
              />
            </div>
            <label className="flex items-center gap-2 cursor-pointer select-none py-1" data-testid="remember-me-label">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                className="w-4 h-4 accent-orange-600"
                data-testid="login-remember"
              />
              <span className="text-sm font-semibold text-slate-300">Ingelogd blijven op dit apparaat (30 dagen)</span>
            </label>
            <button
              type="submit"
              disabled={loading}
              className="pb-btn-primary w-full flex items-center justify-center gap-2"
              data-testid="login-submit"
            >
              <LogIn size={18} /> {loading ? "Bezig…" : "Inloggen"}
            </button>
            <div className="text-center text-sm text-slate-400 font-semibold pt-2">
              Nog geen account? <a href="/signup" className="text-orange-500 font-bold hover:underline">Registreren →</a>
            </div>
          </form>
          <div className="pt-4 border-t border-slate-800 mt-4">
            <div className="text-center text-[11px] font-bold text-slate-500 space-x-3">
              <Link to="/privacy" className="hover:text-orange-500 underline decoration-dotted" data-testid="login-privacy-link">Privacyverklaring</Link>
              <span className="text-slate-700">·</span>
              <Link to="/voorwaarden" className="hover:text-orange-500 underline decoration-dotted" data-testid="login-terms-link">Algemene voorwaarden</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
